import axios from "axios";
import { handleUnauthorized } from "@/lib/silentSso";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "qf_token";
export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use((r) => r, (err) => {
  if (err.response?.status === 401) {
    handleUnauthorized({ url: err.config?.url, method: err.config?.method, hadToken: !!tokenStore.get(),
      setToken: tokenStore.set, clearToken: tokenStore.clear });
  }
  return Promise.reject(err);
});

// --- Auth ---
export const registerEmail = (p) => api.post("/auth/register", p).then(r => r.data);
export const loginEmail = (p) => api.post("/auth/login", p).then(r => r.data);
export const fetchMe = () => api.get("/auth/me").then(r => r.data);
// Ends the gateway session cookie too (single sign-on) — otherwise the common
// login page would silently sign the user straight back in.
export const gatewayLogout = () =>
  fetch(`${process.env.REACT_APP_GATEWAY_URL}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});

// Sign-out from this app returns to the dashboard's Apps page (same origin as the common login page). The
// gateway session is deliberately kept alive because that page needs it; the dashboard's own "Sign out"
// ends it for every app.
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || "http://localhost:3007/login";
export const leaveToDashboard = () => {
  tokenStore.clear();
  window.location.replace(`${new URL(LOGIN_URL).origin}/dashboard?page=apps`);
};

export const logout = () => api.post("/auth/logout").then(r => r.data).finally(leaveToDashboard);
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email }).then(r => r.data);
export const resetPassword = (token, new_password) => api.post("/auth/reset-password", { token, new_password }).then(r => r.data);

// --- MFA (authenticator app 2FA) ---
export const mfaSetup = () => api.post("/auth/mfa/setup").then(r => r.data);
export const mfaEnable = (code) => api.post("/auth/mfa/enable", { code }).then(r => r.data);
export const mfaDisable = (password, code) => api.post("/auth/mfa/disable", { password, code }).then(r => r.data);
export const mfaRegenerateBackupCodes = (password, code) =>
  api.post("/auth/mfa/backup-codes/regenerate", { password, code }).then(r => r.data);
export const mfaVerifyLogin = (mfa_token, code) => api.post("/auth/mfa/verify", { mfa_token, code }).then(r => r.data);

// --- Onboarding ---
export const getOnboardingOptions = () => api.get("/onboarding/options").then(r => r.data);
export const submitOnboarding = (p) => api.post("/onboarding", p).then(r => r.data);
export const submitOnboardingState = (state) => api.post("/onboarding/state", state).then(r => r.data);

// --- Peer review (community) ---
export const submitForPeerReview = (p) => api.post("/peer-reviews/submit", p).then(r => r.data);
export const deletePeerReviewSubmission = (id) => api.delete(`/peer-reviews/${id}`).then(r => r.data);
export const peerReviewQueue = (limit = 10) =>
  api.get(`/peer-reviews/queue?limit=${limit}`).then(r => r.data);
export const decidePeerReview = (p) => api.post("/peer-reviews/decide", p).then(r => r.data);
export const myPeerReviewStats = () => api.get("/peer-reviews/me").then(r => r.data);
export const myPeerReviewSubmissions = () => api.get("/peer-reviews/my-submissions").then(r => r.data);
export const myPeerReviewModuleStatus = () => api.get("/peer-reviews/my-module-status").then(r => r.data);

export const myNotifications = (limit = 30) => api.get(`/notifications?limit=${limit}`).then(r => r.data);
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`).then(r => r.data);
export const markAllNotificationsRead = () => api.post("/notifications/read-all").then(r => r.data);
export const adminBroadcastNotification = (body) => api.post("/admin/notifications/broadcast", body).then(r => r.data);
export const myPeerReviewDecisions = () => api.get("/peer-reviews/my-decisions").then(r => r.data);
export const updatePeerReviewDecision = (decisionId, patch) =>
  api.patch(`/peer-reviews/decisions/${decisionId}`, patch).then(r => r.data);
export const uploadPeerReviewVoice = async (blob, filename) => {
  const fd = new FormData();
  fd.append("audio", blob, filename || "voice-feedback.webm");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/peer-reviews/voice-feedback`, fd, {
    withCredentials: true,
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
  return r.data;
};
export const peerReviewVoiceUrl = (id) => `${API}/peer-reviews/voice-feedback/${id}`;
export const fetchPeerReviewVoiceBlobUrl = async (id) => {
  const r = await api.get(`/peer-reviews/voice-feedback/${id}`, { responseType: "blob" });
  return URL.createObjectURL(r.data);
};

// --- Community (Quran Companion + Circles + community feedback) ---
export const generateCompanionCode = () => api.post("/community/companion/code").then(r => r.data);
export const joinCompanion = (code) => api.post("/community/companion/join", { code }).then(r => r.data);
export const myCompanion = () => api.get("/community/companion").then(r => r.data);
export const leaveCompanion = () => api.delete("/community/companion").then(r => r.data);

export const createCircle = (p) => api.post("/community/circles", p).then(r => r.data);
export const myCircles = () => api.get("/community/circles").then(r => r.data);
export const updateCircle = (id, p) => api.put(`/community/circles/${id}`, p).then(r => r.data);
export const deleteCircle = (id) => api.delete(`/community/circles/${id}`).then(r => r.data);
export const joinCircle = (id, code) => api.post(`/community/circles/${id}/join`, { code }).then(r => r.data);
export const joinCircleByCode = (code) => api.post("/community/circles/join", { code }).then(r => r.data);
export const addCircleMember = (id, email) => api.post(`/community/circles/${id}/members`, { email }).then(r => r.data);
export const removeCircleMember = (id, userId) => api.delete(`/community/circles/${id}/members/${userId}`).then(r => r.data);

// --- Community invite accept/reject (consent-based joining) ---
export const myCompanionRequests = () => api.get("/community/companion/requests").then(r => r.data);
export const acceptCompanionRequest = (id) => api.post(`/community/companion/requests/${id}/accept`).then(r => r.data);
export const rejectCompanionRequest = (id) => api.post(`/community/companion/requests/${id}/reject`).then(r => r.data);
export const circleJoinRequests = (circleId) => api.get(`/community/circles/${circleId}/requests`).then(r => r.data);
export const acceptCircleJoinRequest = (circleId, requestId) => api.post(`/community/circles/${circleId}/requests/${requestId}/accept`).then(r => r.data);
export const rejectCircleJoinRequest = (circleId, requestId) => api.post(`/community/circles/${circleId}/requests/${requestId}/reject`).then(r => r.data);
export const myCircleInvites = () => api.get("/community/circles/invites/mine").then(r => r.data);
export const acceptCircleInvite = (id) => api.post(`/community/circles/invites/${id}/accept`).then(r => r.data);
export const rejectCircleInvite = (id) => api.post(`/community/circles/invites/${id}/reject`).then(r => r.data);

export const submitCommunityFeedback = (p) => api.post("/community/feedback", p).then(r => r.data);
export const listCommunityFeedback = () => api.get("/community/feedback").then(r => r.data);
export const adminListCommunityFeedback = (status) =>
  api.get(`/admin/community/feedback${status ? `?status=${status}` : ""}`).then(r => r.data);
export const adminDecideCommunityFeedback = (id, status) =>
  api.put(`/admin/community/feedback/${id}`, { status }).then(r => r.data);
export const adminListModuleFeedback = (status) =>
  api.get(`/admin/module-feedback${status ? `?status=${status}` : ""}`).then(r => r.data);
export const adminDecideModuleFeedback = (id, status) =>
  api.put(`/admin/module-feedback/${id}`, { status }).then(r => r.data);

// --- Modules ---
export const listModules = () => api.get("/modules").then(r => r.data);
export const getModule = (code) => api.get(`/modules/${code}`).then(r => r.data);
export const postModuleAttempt = (code, p) => api.post(`/modules/${code}/attempt`, p).then(r => r.data);
export const submitModuleFeedback = (code, p) => api.post(`/modules/${code}/feedback`, p).then(r => r.data);
// Strip the same purely-typographic tatweel (ـ, U+0640) the backend also
// strips before hashing/synthesizing (backend/tts.py::_normalize_tts_text)
// — mirrored here so the *request URL itself* changes too. The TTS route
// responds with a 30-day Cache-Control header; if the URL's `text` still
// contains the tatweel, a browser that already cached the old near-silent
// response for that exact URL keeps serving it from disk forever, even
// after the backend is fixed — only a different URL busts that.
const TATWEEL_RE = /ـ/g;
export const ttsAudioUrl = (text, voice = "onyx") => {
  const clean = (text || "").replace(TATWEEL_RE, "");
  const q = clean.trim() ? clean : text; // never send an empty string
  return `${API}/tts/audio?text=${encodeURIComponent(q)}&voice=${voice}`;
};

// --- Recitation (any module — module_code optional, defaults to module 9) ---
export const uploadRecitation = async (blob, filename, note, moduleCode, passageCode, passageAr) => {
  const fd = new FormData();
  fd.append("audio", blob, filename || "recitation.webm");
  if (note) fd.append("note", note);
  if (moduleCode) fd.append("module_code", moduleCode);
  if (passageCode) fd.append("passage_code", passageCode);
  if (passageAr) fd.append("passage_ar", passageAr);
  const t = tokenStore.get();
  const r = await axios.post(`${API}/recitations/upload`, fd, {
    withCredentials: true,
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
  return r.data;
};
export const myRecitations = () => api.get("/recitations/mine").then(r => r.data);
export const recitationAudioUrl = (id) => `${API}/recitations/${id}/audio`;
// Fetch a recitation audio file with the bearer token and return a blob URL.
// Used by the peer-review page because HTML <audio> elements cannot send
// the Authorization header on cross-origin requests.
export const fetchRecitationBlobUrl = async (id) => {
  const r = await api.get(`/recitations/${id}/audio`, { responseType: "blob" });
  return URL.createObjectURL(r.data);
};

// --- Progress / Upsell ---
export const myProgress = () => api.get("/progress/me").then(r => r.data);
// Public, unauthenticated mirror of myProgress()'s `community` block — powers
// the Landing page's "Community & Live Learning" section for signed-out
// visitors (see routes/progress.py's community_config).
export const communityConfig = () => api.get("/community/config").then(r => r.data);
export const myUpsell = () => api.get("/upsell/me").then(r => r.data);

// --- Programs ---
export const listPrograms = () => api.get("/programs").then(r => r.data);
export const getProgram = (id) => api.get(`/programs/${id}`).then(r => r.data);
export const enrollIntent = (id) => api.post(`/programs/${id}/enroll-intent`).then(r => r.data);

// --- Certificate ---
export const myCertificate = () => api.get("/certificate/me").then(r => r.data);
export const certificateDownloadUrl = () => `${API}/certificate/me/download`;

// --- Billing (Stripe) ---
export const createCheckoutSession = (origin_url, package_id = "level2_upgrade") =>
  api.post("/billing/create-checkout-session", { package_id, origin_url }).then(r => r.data);
export const getCheckoutStatus = (session_id) =>
  api.get(`/billing/session-status/${session_id}`).then(r => r.data);

// --- User preferences (cross-device sync) ---
export const updateMyPreferences = (patch) =>
  api.patch("/users/me/preferences", patch).then(r => r.data);

// --- User profile ---
export const updateMyProfile = (patch) =>
  api.patch("/users/me/profile", patch).then(r => r.data);

// --- Admin ---
export const adminListUsers = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.role) qs.set("role", params.role);
  if (params.level) qs.set("level", String(params.level));
  if (params.limit) qs.set("limit", String(params.limit));
  return api.get(`/admin/users${qs.toString() ? "?" + qs : ""}`).then(r => r.data);
};
export const adminCreateUser = (body) => api.post("/admin/users", body).then(r => r.data);
export const adminUpdateUser = (id, p) => api.patch(`/admin/users/${id}`, p).then(r => r.data);
export const adminSetUserRole = (id, role, assignedLocales) => api.post(`/admin/users/${id}/role`, { role, assigned_locales: assignedLocales }).then(r => r.data);
export const adminSetUserLevel = (id, level) => api.post(`/admin/users/${id}/level`, { level }).then(r => r.data);
export const adminDeleteUser = (id) => api.delete(`/admin/users/${id}`).then(r => r.data);
export const adminResetUserProgress = (id) => api.post(`/admin/users/${id}/reset-progress`).then(r => r.data);
export const adminResetUserModuleProgress = (id, moduleCode) => api.post(`/admin/users/${id}/reset-progress/${moduleCode}`).then(r => r.data);
export const adminSendResetLink = (id) => api.post(`/admin/users/${id}/send-reset-link`).then(r => r.data);
export const adminSetUserPassword = (id, new_password) => api.post(`/admin/users/${id}/set-password`, { new_password }).then(r => r.data);
export const adminBulkDelete = (user_ids, confirm_token) => api.post("/admin/users/bulk-delete", { user_ids, confirm_token }).then(r => r.data);
export const adminBulkAssign = (user_ids, payload) => api.post("/admin/users/bulk-assign", { user_ids, ...payload }).then(r => r.data);
export const adminBulkUpload = (file) => {
  const fd = new FormData(); fd.append("file", file);
  return api.post("/admin/users/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const adminListRecitations = (status = "pending", { moduleCode, q } = {}) => {
  const params = new URLSearchParams({ status });
  if (moduleCode) params.set("module_code", moduleCode);
  if (q) params.set("q", q);
  return api.get(`/admin/recitations?${params.toString()}`).then(r => r.data);
};
export const adminDecideRecitation = (id, p) => api.post(`/admin/recitations/${id}/decide`, p).then(r => r.data);
export const adminAuditLog = (action = "", limit = 100) => {
  const params = new URLSearchParams({ limit });
  if (action) params.set("action", action);
  return api.get(`/admin/audit-log?${params.toString()}`).then(r => r.data);
};

// Organizations (institutions)
export const adminListOrganizations = () => api.get("/admin/organizations").then(r => r.data);
export const adminCreateOrganization = (body) => api.post("/admin/organizations", body).then(r => r.data);
export const adminUpdateOrganization = (id, body) => api.put(`/admin/organizations/${id}`, body).then(r => r.data);
export const adminDeleteOrganization = (id) => api.delete(`/admin/organizations/${id}`).then(r => r.data);

export const myOrganizations = () => api.get("/organizations/mine").then(r => r.data);
export const joinOrganization = (code) => api.post("/organizations/join", { code }).then(r => r.data);
export const orgJoinRequests = (orgId) => api.get(`/organizations/${orgId}/requests`).then(r => r.data);
export const acceptOrgJoinRequest = (orgId, requestId) => api.post(`/organizations/${orgId}/requests/${requestId}/accept`).then(r => r.data);
export const rejectOrgJoinRequest = (orgId, requestId) => api.post(`/organizations/${orgId}/requests/${requestId}/reject`).then(r => r.data);
export const orgMembers = (orgId) => api.get(`/organizations/${orgId}/members`).then(r => r.data);
export const removeOrgMember = (orgId, userId) => api.delete(`/organizations/${orgId}/members/${userId}`).then(r => r.data);
export const setOrgMemberRole = (orgId, userId, orgRole) => api.post(`/organizations/${orgId}/members/${userId}/role`, { org_role: orgRole }).then(r => r.data);
export const adminListCertificates = () => api.get("/admin/certificates").then(r => r.data);
export const adminListPrograms = () => api.get("/admin/programs").then(r => r.data);
export const adminCreateProgram = (p) => api.post("/admin/programs", p).then(r => r.data);
export const adminUpdateProgram = (id, p) => api.put(`/admin/programs/${id}`, p).then(r => r.data);
export const adminDeleteProgram = (id) => api.delete(`/admin/programs/${id}`).then(r => r.data);
export const adminGetConfig = () => api.get("/admin/config").then(r => r.data);
export const adminUpdateConfig = (p) => api.patch("/admin/config", p).then(r => r.data);
export const adminAnalytics = () => api.get("/admin/analytics").then(r => r.data);
export const adminAnalyticsTimeseries = (params) => api.get("/admin/analytics/timeseries", { params }).then(r => r.data);

// --- Admin: Modules CMS ---
export const adminListModules = () => api.get("/admin/modules").then(r => r.data);
export const adminGetModule = (code) => api.get(`/admin/modules/${code}`).then(r => r.data);
export const adminCreateModule = (p) => api.post("/admin/modules", p).then(r => r.data);
export const adminUpdateModule = (code, p) => api.put(`/admin/modules/${code}`, p).then(r => r.data);
export const adminDeleteModule = (code) => api.delete(`/admin/modules/${code}`).then(r => r.data);
export const adminUploadModuleImage = async (code, file) => {
  const fd = new FormData(); fd.append("image", file, file.name || "image.jpg");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/modules/${code}/image`, fd, { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data;
};
export const adminUploadItemAudio = async (code, idx, file) => {
  const fd = new FormData(); fd.append("audio", file, file.name || "audio.mp3");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/modules/${code}/items/${idx}/audio`, fd, { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data;
};
export const adminClearItemAudio = (code, idx) =>
  api.delete(`/admin/modules/${code}/items/${idx}/audio`).then(r => r.data);
export const adminBulkUploadItemAudio = async (code, files, startIdx = 0) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f, f.name));
  fd.append("start_idx", String(startIdx));
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/modules/${code}/items/audio/bulk`, fd, { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data;
};
export const adminUploadPassageAudio = async (code, idx, file) => {
  const fd = new FormData(); fd.append("audio", file, file.name || "passage.mp3");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/modules/${code}/passages/${idx}/audio`, fd, { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data;
};
export const adminUpdateInstructionsText = (code, locale, text) =>
  api.put(`/admin/modules/${code}/instructions`, { locale, text }).then(r => r.data);
export const adminUploadInstructionsAudio = async (code, locale, file) => {
  const fd = new FormData(); fd.append("audio", file, file.name || "instructions.mp3");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/modules/${code}/instructions/${locale}/audio`, fd, { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data;
};
export const adminClearInstructionsAudio = (code, locale) =>
  api.delete(`/admin/modules/${code}/instructions/${locale}/audio`).then(r => r.data);
export const adminUploadAyahAudio = async (code, pidx, aidx, file) => {
  const fd = new FormData(); fd.append("audio", file, file.name || "ayah.mp3");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/modules/${code}/passages/${pidx}/ayahs/${aidx}/audio`, fd, { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data;
};
export const adminClearAyahAudio = (code, pidx, aidx) =>
  api.delete(`/admin/modules/${code}/passages/${pidx}/ayahs/${aidx}/audio`).then(r => r.data);
export const adminUploadAyahNewWordsAudio = async (code, pidx, aidx, file) => {
  const fd = new FormData(); fd.append("audio", file, file.name || "new-words.mp3");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/modules/${code}/passages/${pidx}/ayahs/${aidx}/new-words-audio`, fd, { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data;
};
export const adminClearAyahNewWordsAudio = (code, pidx, aidx) =>
  api.delete(`/admin/modules/${code}/passages/${pidx}/ayahs/${aidx}/new-words-audio`).then(r => r.data);
export const adminUpdateAyah = (code, pidx, aidx, p) =>
  api.put(`/admin/modules/${code}/passages/${pidx}/ayahs/${aidx}`, p).then(r => r.data);
export const adminAddAyah = (code, pidx) =>
  api.post(`/admin/modules/${code}/passages/${pidx}/ayahs`).then(r => r.data);
export const adminDeleteAyah = (code, pidx, aidx) =>
  api.delete(`/admin/modules/${code}/passages/${pidx}/ayahs/${aidx}`).then(r => r.data);
export const adminUploadItemPicture = async (code, idx, file) => {
  const fd = new FormData(); fd.append("image", file, file.name || "picture.jpg");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/modules/${code}/items/${idx}/picture`, fd, { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data;
};
export const adminClearItemPicture = (code, idx) =>
  api.delete(`/admin/modules/${code}/items/${idx}/picture`).then(r => r.data);

// --- Admin: Learn-by-Group CMS ---
export const adminCreateGroup = (code, p) => api.post(`/admin/modules/${code}/groups`, p).then(r => r.data);
export const adminUpdateGroup = (code, groupId, p) => api.put(`/admin/modules/${code}/groups/${groupId}`, p).then(r => r.data);
export const adminDeleteGroup = (code, groupId) => api.delete(`/admin/modules/${code}/groups/${groupId}`).then(r => r.data);
export const adminUploadGroupAudio = async (code, groupId, file) => {
  const fd = new FormData(); fd.append("audio", file, file.name || "group-audio.webm");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/modules/${code}/groups/${groupId}/audio`, fd, { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data;
};
export const adminClearGroupAudio = (code, groupId) =>
  api.delete(`/admin/modules/${code}/groups/${groupId}/audio`).then(r => r.data);

// --- Achievements ---
export const fetchAchievements = () => api.get("/achievements").then(r => r.data);

// --- Admin: TTS warm-cache ---
export const adminTtsPreview = () => api.get("/admin/tts/preview").then(r => r.data);
export const adminTtsWarmBatch = (texts, voice = "onyx", model = "tts-1") =>
  api.post("/admin/tts/warm-batch", { texts, voice, model }).then(r => r.data);

// --- Admin: Certificate templates CMS ---
export const adminListCertificateTemplates = () =>
  api.get("/admin/certificate-templates").then(r => r.data);
export const adminGetActiveCertificateTemplate = () =>
  api.get("/admin/certificate-templates/active").then(r => r.data);
export const adminUploadCertificateTemplate = async (meta, file) => {
  const fd = new FormData();
  fd.append("name", meta.name || "Default");
  fd.append("accent_color", meta.accent_color || "#064E3B");
  fd.append("title", meta.title || "Quran Fluency Certificate");
  fd.append("subtitle", meta.subtitle || "This certifies that");
  fd.append("show_qr", meta.show_qr === false ? "false" : "true");
  fd.append("background", file, file.name || "certificate.png");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/certificate-templates`, fd, {
    withCredentials: true,
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
  return r.data;
};
export const adminActivateCertificateTemplate = (id) =>
  api.post(`/admin/certificate-templates/${id}/activate`).then(r => r.data);
// Preview helpers — fetch the PDF as a blob and return an object-URL so the
// caller can `window.open(url)` to view it inline.
export const adminPreviewActiveCertificateTemplate = async () => {
  const r = await api.get("/admin/certificate-templates/active/preview", { responseType: "blob" });
  return URL.createObjectURL(r.data);
};
export const adminPreviewCertificateTemplate = async (id) => {
  const r = await api.get(`/admin/certificate-templates/${id}/preview`, { responseType: "blob" });
  return URL.createObjectURL(r.data);
};
export const adminPreviewDraftCertificateTemplate = async (meta) => {
  const fd = new FormData();
  fd.append("name", meta.name || "Default");
  fd.append("accent_color", meta.accent_color || "#064E3B");
  fd.append("title", meta.title || "Quran Fluency Certificate");
  fd.append("subtitle", meta.subtitle || "This certifies that");
  fd.append("show_qr", meta.show_qr === false ? "false" : "true");
  const t = tokenStore.get();
  const r = await axios.post(`${API}/admin/certificate-templates/preview-draft`, fd, {
    withCredentials: true,
    responseType: "blob",
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
  return URL.createObjectURL(r.data);
};

// --- i18n (admin-editable localization) ---
export const fetchPublishedLocales = () => api.get("/i18n/locales").then(r => r.data);
export const fetchResolvedLocale = (code) => api.get(`/i18n/${code}`).then(r => r.data);
export const adminListLocales = () => api.get("/admin/i18n/locales").then(r => r.data);
export const adminAddLocale = (p) => api.post("/admin/i18n/locales", p).then(r => r.data);
export const adminSetLocaleStatus = (code, status) => api.patch(`/admin/i18n/locales/${code}`, { status }).then(r => r.data);
export const adminLocaleEditor = (code) => api.get(`/admin/i18n/${code}`).then(r => r.data);
export const adminSaveOverride = (code, key, value) => api.put(`/admin/i18n/${code}/override`, { key, value }).then(r => r.data);
export const adminGenerateBase = (code) => api.post(`/admin/i18n/${code}/generate`).then(r => r.data);

// --- Translator applications ---
export const submitTranslatorApplication = (body) => api.post("/translator-applications", body).then(r => r.data);
export const adminListTranslatorApplications = (status) =>
  api.get("/admin/translator-applications", { params: status ? { status } : {} }).then(r => r.data);
export const adminDecideTranslatorApplication = (id, status) =>
  api.put(`/admin/translator-applications/${id}`, { status }).then(r => r.data);
export const adminGenerateModuleLocale = (moduleCode, locale) => api.post(`/admin/modules/${moduleCode}/generate/${locale}`).then(r => r.data);
export const adminGetModuleTranslations = (moduleCode, locale) => api.get(`/admin/modules/${moduleCode}/translations/${locale}`).then(r => r.data);
export const adminSetModuleTranslations = (moduleCode, locale, body) => api.put(`/admin/modules/${moduleCode}/translations/${locale}`, body).then(r => r.data);
// Admin-gated download — needs the bearer token, so a plain <a href> won't
// work; fetch as a blob and trigger the save client-side instead.
export const adminExportI18nCsv = async (code) => {
  const r = await api.get(`/admin/i18n/${code}/export.csv`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `translations-${code}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
export const adminImportI18nCsv = (code, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/admin/i18n/${code}/import`, fd).then(r => r.data);
};
